import type { NextPage, GetStaticProps, GetStaticPaths } from 'next';
import * as Sentry from '@sentry/nextjs';
import { publishMode, revalidate, domain, fallback } from '@/constants/config';
import type { PageProps, QueryData, AllPageData } from '@/types';
import DefaultErrorPage from 'next/error';
import { useIsPreviewing, BuilderComponent } from '@builder.io/react';
import { useEffect, useRef } from 'react';
import { init as initFeathery } from '@feathery/react';
import { restRequest } from '@/utils/restRequest';
import { useColorProvider } from '@/utils/ColorProvider';
import { useGeoData } from '@/utils/useGeoData';
import PageHead from '@/components/Head';
import Footer from '@/components/Footer';
import Loader from '@/components/Loader';
import { usePostHog } from '@/lib/posthogProvider';
import { GeneralError } from '@/types/errors';
import { useUtmParams } from '@/utils/utmParams';
import { extractComponentNames, extractFormIds, findBlockByComponentName } from '@/utils/builderPageInfo';

// set revalidate from env. This determines how long the pages will cache and
// therefore how long changes will take to appear

export const getStaticProps: GetStaticProps = async ({
  params
}) => {
  let pageArr: Array<object>;
  let duplicatePaths: Array<string> = [];
  const urlPath = Array.isArray(params?.page) ? `/${params.page.join('/') || ''}` : `/${params?.page || ''}`;
  try {
    if (!!publishMode) {
      // Pull page specific to env domain if in published mode
      // Then filter out correct page by its publishedUrl
      const domainPagesArr = await restRequest<QueryData<'page'>[]>({
        modelName: 'custom-page',
        query: `data.domain.id=${domain}`
      });
      pageArr = domainPagesArr?.filter(page => page.data?.publishedUrl === urlPath);

      // check for duplicate paths for more visible error in publishMode
      const paths = domainPagesArr.map(item => item.data?.publishedUrl).filter((path): path is string => path !== undefined);
      duplicatePaths = paths.filter((item, index) => paths.indexOf(item) !== index);
    } else {
      pageArr = await restRequest<QueryData<'page'>[]>({
        modelName: 'custom-page',
        userAttributes: `urlPath=${urlPath}`
      });
    }
  } catch (err) {
    // error bubbled up from restRequest
    Sentry.captureException(err);
    pageArr = [];
  }
  const page = pageArr?.length && pageArr.length > 0 ? pageArr[0] : null;
  const pageFormIds = page ? extractFormIds(page) : [];
  const pageComponents = page ? extractComponentNames(page) : [];
  try {
    return {
      props: {
        content: {
          page
        },
        duplicate: duplicatePaths,
        forms: pageFormIds,
        pageComponents
      },
      revalidate
    };
  } catch (err) {
    // error bubbled up from restRequest
    Sentry.captureException(err);
    return {
      props: {
        content: {
          page: null
        },
        duplicate: duplicatePaths,
        forms: [],
        pageComponents: []
      },
      revalidate
    };
  }
};

/**
 * @note Using alias to match data definition
 */
export const getStaticPaths: GetStaticPaths = async () => {
  let pagesArr;
  if (publishMode) {
    // Pull pages specific to env domain if in published mode
    // When published we use publishedUrl rather than standard url
    try {
      pagesArr = await restRequest<AllPageData>({
        modelName: 'custom-page',
        query: `data.domain.id=${domain}`,
        fields: 'data.publishedUrl,name,id'
      });
      const publishedPages = pagesArr?.filter(page => !!page.data?.publishedUrl);
      const publishedPathsArr = publishedPages?.map(page => `${page.data?.publishedUrl}`);
      return {
        paths: publishedPathsArr,
        fallback: false
      };
    } catch (err) {
      // error bubbled up from restRequest
      Sentry.captureException(err);
      return {
        paths: [],
        fallback: false
      };
    }
  }
  // Pull all page urls

  try {
    pagesArr = await restRequest<AllPageData>({
      modelName: 'custom-page',
      fields: 'data,name,id'
    });
    const pagePaths = pagesArr?.filter(page => !!page.data?.url && page.data?.url !== '/404');
    return {
      paths: pagePaths?.map(page => `${page.data?.url}`) || [],
      fallback
    };
  } catch (err) {
    // error bubbled up from restRequest
    Sentry.captureException(err);
    return {
      paths: [],
      fallback
    };
  }
};

// Separate component to hold all hooks - only rendered when we have valid content
const PageContent = ({
  content,
  duplicate,
  forms,
  isPreviewing,
  pageComponents = []
}: PageProps & {
  isPreviewing: boolean;
}) => {
  const featheryInitializedRef = useRef(false);

  // Initialize Feathery synchronously (before children mount). Only runs once per page mount.
  if (typeof window !== 'undefined' && !featheryInitializedRef.current) {
    const sdk = process.env.NEXT_PUBLIC_FEATHERY_SDK;
    if (typeof sdk === 'string') {
      try {
        initFeathery(sdk, {
          preloadForms: forms || []
        });
        featheryInitializedRef.current = true;
      } catch (error) {
        Sentry.withScope(scope => {
          scope.setTag('api', 'feathery');
          scope.setTag('function', 'initFeathery');
          scope.setLevel('error');
          scope.setContext('feathery_init', {
            formsToPreload: forms,
            formsCount: forms?.length || 0
          });
          Sentry.captureException(error);
        });
      }
    }
  }
  const posthog = usePostHog();
  const utmParams = useUtmParams();
  const {
    setColors
  } = useColorProvider();

  // Track timing for geo data loading
  const mountTimeRef = useRef<number>(Date.now());
  const geoDataTimingTrackedRef = useRef<boolean>(false);

  // Get usesGeoData early, with fallback to false if content/page/data is not available
  const usesGeoData = content?.page?.data?.usesGeoData ?? false;
  const {
    stateUSA,
    isLoading
  } = useGeoData(!!usesGeoData);
  useEffect(() => {
    // Track page details when component mounts
    posthog?.capture('page_viewed', {
      title: content?.page?.data?.title,
      url: content?.page?.data?.url,
      isPreview: isPreviewing,
      hasFooter: !!content?.page?.data?.footer,
      formCount: forms?.length || 0
    });
  }, [content, isPreviewing, forms, posthog]);

  // Track geo data loading performance
  useEffect(() => {
    if (usesGeoData && !isLoading && !geoDataTimingTrackedRef.current) {
      const loadTime = Date.now() - mountTimeRef.current;
      geoDataTimingTrackedRef.current = true;
      posthog?.capture('geo_data_load_complete', {
        load_time_ms: loadTime,
        stateUSA: stateUSA,
        title: content?.page?.data?.title,
        url: content?.page?.data?.url,
        is_preview: isPreviewing
      });
    }
  }, [usesGeoData, isLoading, stateUSA, content, isPreviewing, posthog]);

  // visibly error if we have duplicate slugs. Should only happen in
  // publish mode
  if (duplicate.length > 0) {
    Sentry.withScope(scope => {
      scope.setLevel('warning');
      scope.setContext('duplicates', {
        paths: JSON.stringify(duplicate),
        currentPath: content?.page?.data?.url,
        currentTitle: content?.page?.data?.title
      });
      Sentry.captureMessage(`Duplicate published paths detected: ${duplicate.join(', ')}`);
    });

    // eslint-disable-next-line no-console
    console.warn('Duplicate published paths detected. This may cause issues with SEO and display the wrong page:', JSON.stringify(duplicate));
  }
  const {
    title,
    description,
    shareImage,
    url,
    primaryAccentColor,
    secondaryAccentColor,
    footer,
    isLightMode,
    preventIndexing,
    footerBackgroundColor,
    footerTextColor,
    footerVersion,
    thankYouPageBackgroundColor
  } = content.page?.data || {};
  useEffect(() => {
    setColors({
      primaryAccentColor,
      secondaryAccentColor
    });
    if (thankYouPageBackgroundColor) {
      sessionStorage.setItem('thankYouPageBackgroundColor', thankYouPageBackgroundColor);
    } else {
      sessionStorage.removeItem('thankYouPageBackgroundColor');
    }
  }, []);
  useEffect(() => {
    const headerBlock = findBlockByComponentName(content.page?.data?.blocks, ['Header', 'HeaderV2']);
    if (headerBlock?.component?.options?.isSticky) {
      sessionStorage.setItem('isSticky', 'true');
    } else {
      sessionStorage.setItem('isSticky', 'false');
    }
  }, []);
  return <div className="pageWrapper" data-sentry-component="PageContent" data-sentry-source-file="[[...page]].tsx">
			<PageHead path={url || '/'} title={title || ''} description={description || ''} shareImage={shareImage} preventIndexing={preventIndexing} data-sentry-element="PageHead" data-sentry-source-file="[[...page]].tsx" />
			{/* Show loading state or render BuilderComponent based on geo data loading state */}
			<div className="mainContent">
				{usesGeoData && isLoading ? <Loader loadingText="Loading..." /> : <BuilderComponent content={content.page} model="custom-page" options={{
        includeRefs: true
      }} data={{
        ...utmParams,
        stateUSA: utmParams.utmUsState || stateUSA || 'US',
        // Fallback to 'US' when stateUSA is null,
        pageComponents
      }} />}
			</div>
			<Footer footerVersion={footerVersion} isLightMode={isLightMode} copy={footer?.value.data.copy || ''} links={footer?.value.data.links || []} footerBackgroundColor={footerBackgroundColor} footerTextColor={footerTextColor} pageComponents={pageComponents} data-sentry-element="Footer" data-sentry-source-file="[[...page]].tsx" />
		</div>;
};
export const Page: NextPage<PageProps> = ({
  content,
  duplicate,
  forms,
  pageComponents
}) => {
  const isPreviewing = useIsPreviewing();

  // 404 if no page found - check early before other hooks
  if (!content || !content.page && !isPreviewing) {
    if (process.env.VERCEL_ENV === 'production') {
      Sentry.captureMessage(GeneralError.NoPageFound, {
        level: 'error'
      });
    }
    return <DefaultErrorPage statusCode={404} />;
  }
  return <PageContent content={content} duplicate={duplicate} forms={forms} isPreviewing={isPreviewing} pageComponents={pageComponents} data-sentry-element="PageContent" data-sentry-component="Page" data-sentry-source-file="[[...page]].tsx" />;
};
export default Page;